const FARO_SESSION_PREFIX = "scs-faro-session:";
const FARO_SESSION_LEGACY_KEY = "scs-faro-session";
const FARO_ACTIVE_SCOPE_KEY = "scs-faro-active-scope";
const FARO_ORDER_FLAG_KEY = "scs-faro-custom-order-active";
const FARO_RELOAD_RESET_KEY = "scs-faro-reload-reset-done";
const MAX_IMAGE_BYTES = 400000;

export const FARO_SCOPES = {
  POSSCREEN: "posscreen",
  PRODUCTS: "products",
  QUICKVIEW: "quickview",
  MENU: "menu",
};

const storageKeyForScope = (scope) =>
  `${FARO_SESSION_PREFIX}${scope || FARO_SCOPES.MENU}`;

const clearAllFaroSessionKeys = () => {
  try {
    sessionStorage.removeItem(FARO_SESSION_LEGACY_KEY);
    sessionStorage.removeItem(FARO_ACTIVE_SCOPE_KEY);
    sessionStorage.removeItem(FARO_RELOAD_RESET_KEY);
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(FARO_SESSION_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    /* ignore */
  }
};

export const getFaroActiveScope = () => {
  try {
    return sessionStorage.getItem(FARO_ACTIVE_SCOPE_KEY) || FARO_SCOPES.MENU;
  } catch {
    return FARO_SCOPES.MENU;
  }
};

export const setFaroActiveScope = (scope) => {
  try {
    sessionStorage.setItem(
      FARO_ACTIVE_SCOPE_KEY,
      scope || FARO_SCOPES.MENU
    );
  } catch {
    /* ignore */
  }
};

/** Resolve which Faro bucket to use for this open (POS screen / products / Quickview / menu). */
export const resolveFaroScope = (routeState = {}) => {
  if (routeState?.fromQuickview) return FARO_SCOPES.QUICKVIEW;
  if (routeState?.fromPosscreen) return FARO_SCOPES.POSSCREEN;
  if (routeState?.fromProducts) return FARO_SCOPES.PRODUCTS;
  // Returning from custom-order flow → keep the screen that started Faro
  if (routeState?.fromCustomOrder || routeState?.fromFaro) {
    return getFaroActiveScope();
  }
  return FARO_SCOPES.MENU;
};

// Runs once per full document load (not on SPA remount / minimize reopen).
// Browser reload → wipe all faro UI sessions so faro opens in initial tab state.
try {
  const navEntry = performance.getEntriesByType?.("navigation")?.[0];
  const isBrowserReload =
    (navEntry && navEntry.type === "reload") ||
    (typeof performance !== "undefined" &&
      performance.navigation &&
      performance.navigation.type === 1);
  if (isBrowserReload) {
    clearAllFaroSessionKeys();
  } else {
    // Drop legacy shared key so old cross-screen data cannot leak
    sessionStorage.removeItem(FARO_SESSION_LEGACY_KEY);
  }
} catch {
  /* ignore */
}

export const loadFaroSession = (scope) => {
  try {
    const raw = sessionStorage.getItem(storageKeyForScope(scope));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const saveFaroSession = (session, scope) => {
  try {
    const resolved = scope || FARO_SCOPES.MENU;
    setFaroActiveScope(resolved);
    const payload = { ...session, scope: resolved };
    if (
      payload.imageData &&
      typeof payload.imageData === "string" &&
      payload.imageData.length > MAX_IMAGE_BYTES
    ) {
      payload.imageData = "";
    }
    sessionStorage.setItem(storageKeyForScope(resolved), JSON.stringify(payload));
  } catch (err) {
    console.log(err, "faro session save failed");
  }
};

export const clearFaroSession = (scope) => {
  try {
    if (scope) {
      sessionStorage.removeItem(storageKeyForScope(scope));
      return;
    }
    clearAllFaroSessionKeys();
  } catch {
    /* ignore */
  }
};

/** Clear Faro buckets that should reset only after POS checkout completes. */
export const clearFaroSessionsAfterCheckout = () => {
  clearFaroSession(FARO_SCOPES.POSSCREEN);
  clearFaroSession(FARO_SCOPES.QUICKVIEW);
};

/**
 * Keep POS-screen Faro cart badge in sync with live POS cart.
 * Empty cart → clear confirmed count so Faro hides the bag icon.
 */
export const syncPosscreenFaroConfirmedFromCart = (cartItems) => {
  try {
    const session = loadFaroSession(FARO_SCOPES.POSSCREEN);
    if (!session) return;

    const cart = Array.isArray(cartItems) ? cartItems : [];
    const productCounts = {};
    cart.forEach((item) => {
      const id =
        item?.stock_id ||
        item?.product_id ||
        item?.mother_product_id ||
        item?._id ||
        item?.SKU ||
        item?.sku ||
        "";
      if (!id) return;
      const qty = Math.max(1, Number(item?.Qty) || 1);
      productCounts[id] = (productCounts[id] || 0) + qty;
    });

    const payload = {
      ...session,
      posConfirmedCount: cart.length,
      posConfirmedProductCounts: productCounts,
    };
    sessionStorage.setItem(
      storageKeyForScope(FARO_SCOPES.POSSCREEN),
      JSON.stringify(payload)
    );
  } catch {
    /* ignore */
  }
};

export const setFaroCustomOrderActive = (active = true) => {
  try {
    if (active) {
      sessionStorage.setItem(FARO_ORDER_FLAG_KEY, "1");
    } else {
      sessionStorage.removeItem(FARO_ORDER_FLAG_KEY);
    }
  } catch {
    /* ignore */
  }
};

export const isFaroCustomOrderActive = () => {
  try {
    return sessionStorage.getItem(FARO_ORDER_FLAG_KEY) === "1";
  } catch {
    return false;
  }
};

export const clearFaroCustomOrderActive = () => {
  setFaroCustomOrderActive(false);
};

export const buildFaroSessionSnapshot = ({
  mode,
  queryText,
  intent,
  imageData,
  imageName,
  hasSearched,
  products,
  weak,
  activePromptKey,
  selectedPromptChildren,
  orderItems,
  isOrderOpen,
  addedProductCounts,
  posConfirmedCount,
  posConfirmedProductCounts,
}) => ({
  mode,
  queryText,
  intent,
  imageData: imageData || "",
  imageName: imageName || "",
  hasSearched,
  products: Array.isArray(products) ? products : [],
  weak: Boolean(weak),
  activePromptKey: activePromptKey || "",
  selectedPromptChildren: selectedPromptChildren || {},
  orderItems: Array.isArray(orderItems) ? orderItems : [],
  isOrderOpen: Boolean(isOrderOpen),
  addedProductCounts:
    addedProductCounts && typeof addedProductCounts === "object"
      ? addedProductCounts
      : {},
  posConfirmedCount: Number(posConfirmedCount) || 0,
  posConfirmedProductCounts:
    posConfirmedProductCounts && typeof posConfirmedProductCounts === "object"
      ? posConfirmedProductCounts
      : {},
});
